/**
 * PDP hero layout (`enable_hero_slider`): `media-gallery.js` is not loaded, so variant-driven slide
 * changes run here. Resolution order per `syncHeroToVariant`:
 *   1. Shopify admin-assigned `variant.featured_media.id` match against slide `data-media-id`.
 *   2. `featured_media.position` (1-based) — Shopify sometimes omits ids after section fetches.
 *   3. Bonded Matter alt-text option-value match: counts how many of the selected variant's option
 *      values (plus the BM metal display name on the checked metal radio) appear in each slide's
 *      image alt and picks the best. Only upgrades past the admin-assigned match when it strictly
 *      beats it, so merchant-specified per-variant media always wins ties. Mirrors the rule in
 *      `assets/media-gallery.js` so the two PDP layouts behave identically.
 */
(function () {
  'use strict';

  function lc(v) {
    return (v == null ? '' : String(v)).toLowerCase().trim();
  }

  function needleInAlt(needle, alt) {
    if (!needle || !alt) return false;
    if (/^\d+(\.\d+)?$/.test(needle)) {
      var esc = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp('(?:^|[^0-9.])' + esc + '(?:[^0-9]|$)').test(alt);
    }
    return alt.indexOf(needle) !== -1;
  }

  function scoreAgainstNeedles(alt, needles) {
    var lcAlt = lc(alt);
    if (!lcAlt || !needles.length) return 0;
    var score = 0;
    for (var i = 0; i < needles.length; i++) {
      if (needles[i] && needleInAlt(needles[i], lcAlt)) score += 1;
    }
    return score;
  }

  function buildMatchContext(sectionId, variant) {
    var productInfo = document.getElementById('ProductInfo-' + sectionId);
    var BM = window.BmPdpGalleryMatch;
    if (BM && typeof BM.buildMatchContext === 'function') {
      return BM.buildMatchContext(productInfo, variant);
    }
    return { needles: [], sku: '', shapeCode: '', metalCode: '' };
  }

  function slideAltHaystack(slide) {
    if (!slide) return '';
    var fromData = slide.getAttribute('data-bm-match-haystack');
    if (fromData) return fromData;
    var img = slide.querySelector('img');
    return (img && img.getAttribute('alt')) || '';
  }

  // SKU-in-filename haystack. BM merchants upload images whose filename encodes one or more
  // variant SKUs (e.g. `photo-hand-VMRNTQOV186WG.webp`, or `…_2C_VMRNTQOV186WG-1_….webp` for
  // a shared-metal shot). A case-insensitive substring hit against `variant.sku` is the
  // primary deterministic signal even when Shopify's `variant.featured_media` is null.
  function slideSrcHaystack(slide) {
    if (!slide) return '';
    var img = slide.querySelector('img');
    if (!img) return '';
    return img.currentSrc || img.getAttribute('src') || '';
  }

  function scoreSlide(slide, alt, ctx) {
    var BM = window.BmPdpGalleryMatch;
    var haystack = (slideSrcHaystack(slide) || '') + ' ' + (alt || '');
    if (BM && typeof BM.scoreHaystack === 'function') {
      return BM.scoreHaystack(haystack, ctx);
    }
    return scoreAgainstNeedles(alt, ctx.needles || []);
  }

  function findBestBmSlideIndex(slides, ctx) {
    if (!ctx || (!ctx.needles.length && !ctx.sku && !ctx.shapeCode && !ctx.metalCode)) {
      return { index: -1, score: 0, haystack: '' };
    }
    var bestI = -1;
    var bestScore = 0;
    var bestHaystack = '';
    for (var i = 0; i < slides.length; i++) {
      if (slides[i].classList && slides[i].classList.contains('swiper-slide-duplicate')) continue;
      var alt = slideAltHaystack(slides[i]);
      var haystack = (slideSrcHaystack(slides[i]) || '') + ' ' + (alt || '');
      var score = scoreSlide(slides[i], alt, ctx);
      if (score > bestScore) {
        bestScore = score;
        bestI = i;
        bestHaystack = haystack;
      }
    }
    return { index: bestI, score: bestScore, haystack: bestHaystack };
  }

  function featuredSlideIndex(slides, variant) {
    if (!variant) return -1;
    var fm = variant.featured_media || null;

    if (fm && fm.id) {
      for (var i = 0; i < slides.length; i++) {
        if (String(slides[i].getAttribute('data-media-id') || '') === String(fm.id)) {
          return i;
        }
      }
    }

    if (fm && !fm.id && fm.position && fm.position > 0 && fm.position <= slides.length) {
      return fm.position - 1;
    }

    return -1;
  }

  function syncHeroToVariant(sectionId, variant, attempt) {
    if (typeof attempt !== 'number') attempt = 0;
    var root = document.getElementById('BmHeroSlider-' + sectionId);
    if (!root) return;
    var slideshow = root.querySelector('#bm-pdp-hero-slideshow') || root.querySelector('slideshow-component');
    var slider = slideshow && slideshow.sliderInstance && slideshow.sliderInstance.slider;
    if (!slider) {
      if (attempt < 100) {
        window.requestAnimationFrame(function () {
          syncHeroToVariant(sectionId, variant, attempt + 1);
        });
      }
      return;
    }

    var BM = window.BmPdpGalleryMatch;
    var productInfo = document.getElementById('ProductInfo-' + sectionId);
    if (variant && BM && typeof BM.galleryVariantForProductInfo === 'function') {
      variant = BM.galleryVariantForProductInfo(productInfo);
    } else if (variant && BM && typeof BM.variantConflictsDomPicker === 'function' && productInfo) {
      if (BM.variantConflictsDomPicker(productInfo, variant)) variant = null;
    }

    var ctx = buildMatchContext(sectionId, variant);
    var group = BM && BM.filterMediaGroup
      ? BM.filterMediaGroup(slideshow.querySelector('.swiper-wrapper'), '.bm-pdp-hero-slide', ctx, parseInt(root.dataset.bmHeroGalleryLimit, 10))
      : { nodes: Array.prototype.slice.call(root.querySelectorAll('.bm-pdp-hero-slide')), changed: false };
    var slideArr = group.nodes;
    if (group.changed) {
      BM.refreshMediaSlider(slider);
      slider.slideTo(0, 0, false);
    }
    root.dataset.bmHeroSlideCount = String(slideArr.length);
    root.classList.toggle('bm-hero-slider--many-slides', slideArr.length > 10);
    slideshow.classList.toggle('slideshow-controls-visible--yes', slideArr.length > 1);
    var bmMatch = findBestBmSlideIndex(slideArr, ctx);

    if (!variant) {
      if (bmMatch.index >= 0 && bmMatch.index !== slider.realIndex) {
        try {
          slider.slideTo(bmMatch.index, 0, false);
        } catch (e) {
          /* ignore */
        }
      }
      return;
    }

    var featI = featuredSlideIndex(slideArr, variant);
    if (featI >= 0 && BM && !BM.mediaMatchesContext(slideSrcHaystack(slideArr[featI]) + ' ' + slideAltHaystack(slideArr[featI]), ctx)) featI = -1;

    var bestI = -1;
    if (featI >= 0 && bmMatch.index >= 0) {
      var featSlide = slideArr[featI];
      var featAlt = slideAltHaystack(featSlide);
      var featHaystack = (slideSrcHaystack(featSlide) || '') + ' ' + (featAlt || '');
      var featScore = scoreSlide(featSlide, featAlt, ctx);
      var preferred =
        BM && typeof BM.pickTargetFromScores === 'function'
          ? BM.pickTargetFromScores(
              featScore,
              featHaystack,
              {
                score: bmMatch.score,
                haystack: bmMatch.haystack,
                index: bmMatch.index,
              },
              ctx
            )
          : bmMatch.score > featScore
            ? { index: bmMatch.index }
            : null;
      bestI = preferred ? preferred.index : featI;
    } else if (featI >= 0) {
      bestI = featI;
    } else if (bmMatch.index >= 0) {
      bestI = bmMatch.index;
    }

    if (bestI < 0) return;
    if (bestI === slider.realIndex) return;
    try {
      slider.slideTo(bestI, 0, false);
    } catch (e) {
      /* ignore */
    }
  }

  window.BMSyncPdpHeroGalleryToVariant = syncHeroToVariant;

  /**
   * Pagination loading-bar parity: when the active slide is a 3D model, swap the bullet to a
   * longer fill duration so the indicator finishes at the same moment Swiper auto-advances.
   * Class hook is consumed by `bm-overrides.css .bm-pdp-hero-bullet--model`.
   */
  function refreshActiveBulletForModelSlide(root) {
    if (!root) return;
    var slideshow = root.querySelector('#bm-pdp-hero-slideshow') || root.querySelector('slideshow-component');
    if (!slideshow) return;
    var swiper = slideshow.sliderInstance && slideshow.sliderInstance.slider;
    if (!swiper) return;
    var bullets = root.querySelectorAll('.swiper-pagination-bullet');
    if (!bullets || !bullets.length) return;
    var activeIdx = swiper.realIndex || 0;
    var slides = swiper.slides || [];
    var isModel = false;
    if (slides[activeIdx]) {
      var t = slides[activeIdx].getAttribute('data-media-type') || '';
      isModel = t === 'model';
    }
    for (var i = 0; i < bullets.length; i++) {
      bullets[i].classList.remove('bm-pdp-hero-bullet--model');
    }
    var activeBullet = root.querySelector('.swiper-pagination-bullet-active');
    if (activeBullet && isModel) activeBullet.classList.add('bm-pdp-hero-bullet--model');
  }

  function bindHeroPagination(root) {
    if (!root || root.__bmHeroBound) return;
    var slideshow = root.querySelector('#bm-pdp-hero-slideshow') || root.querySelector('slideshow-component');
    var swiper = slideshow && slideshow.sliderInstance && slideshow.sliderInstance.slider;
    if (!swiper) return;
    root.__bmHeroBound = true;
    swiper.on('slideChangeTransitionStart', function () {
      refreshActiveBulletForModelSlide(root);
    });
    refreshActiveBulletForModelSlide(root);
  }

  function initAllHeroes() {
    var roots = document.querySelectorAll('[data-bm-hero-slider]');
    for (var i = 0; i < roots.length; i++) {
      bindHeroPagination(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      // Defer to next tick so slideshow-component has had a chance to init.
      window.requestAnimationFrame(initAllHeroes);
    });
  } else {
    window.requestAnimationFrame(initAllHeroes);
  }

  // Re-attempt up to ~2s in case Swiper init is delayed by deferred bundles.
  var attempts = 0;
  var iv = window.setInterval(function () {
    attempts += 1;
    initAllHeroes();
    if (attempts > 20) window.clearInterval(iv);
  }, 100);
})();
